import { message, Descriptions, Button, Splitter, Tabs } from 'antd'
import { useCallback, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../../api'
import { PageHeader } from '../Common'
import PDFPreview from '../Common/PDFPreview'
import { useAsync, useIsMobile } from '../../hooks'
import { MOBILE_MEDIA_QUERY } from '../../lib/responsive'
import Loading from '../Loading'
import TransmissionSidebar from '../Common/TransmissionSidebar'
import AlertForm from './AlertForm'
import { useSidebar } from '../Layout/SidebarContext'

const { TabPane } = Tabs

const useStyles = createUseStyles({
  app: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    flex: '1 1 auto',
  },
  pdfBox: {
    maxWidth: 'calc(100% - 456px)',
    flexGrow: '1',
    minWidth: '100%',
    position: 'relative',
    height: 'calc(100vh - 96px - 68px)',
    overflowY: 'scroll',
    overflowX: 'hidden',
    '& canvas': {
      maxWidth: '100%',
    },
    // Each pane is the whole page below md, so a viewport-tall scroll box
    // inside the page scroll would just be two scrollbars on one gesture.
    [MOBILE_MEDIA_QUERY]: {
      maxWidth: 'none',
      height: 'auto',
      overflowY: 'visible',
    },
  },
  formBox: {
    position: 'relative',
    overflowY: 'scroll',
    height: 'calc(100vh - 96px - 68px)',
    padding: '0 16px',
    '& > form': {
      marginTop: '-16px',
    },
    [MOBILE_MEDIA_QUERY]: {
      height: 'auto',
      overflowY: 'visible',
      padding: 0,
    },
  },
  patientContainer: {
    padding: '0 16px',
    [MOBILE_MEDIA_QUERY]: {
      padding: 0,
    },
  },
})

export default function Edit() {
  const { id } = useParams()
  const classes = useStyles()
  const navigate = useNavigate()
  const getAlert = useCallback(
    () => api.alerts.get(id, { includeForm: true }),
    [id],
  )
  const { pending, value: alert } = useAsync(getAlert)
  const [isPatientDrawerVisible, setIsPatientDrawerVisible] = useState(false)
  const { setIsSidebarVisible, isSidebarVisible } = useSidebar()
  const isMobile = useIsMobile()
  const [mobileTab, setMobileTab] = useState('form')

  const togglePatientDrawer = () => {
    if (!isPatientDrawerVisible && isSidebarVisible) {
      setIsSidebarVisible(false)
    } else if (isPatientDrawerVisible && !isSidebarVisible) {
      setIsSidebarVisible(true)
    }
    setIsPatientDrawerVisible(!isPatientDrawerVisible)
  }


  const onSubmit = useCallback(
    async values => {
      try {
        await api.alerts.update(id, values)
        message.success('Alert successfully updated')
        navigate(-1)
      } catch (e) {
        message.error('Oops, we had a problem. Please try submitting again')
      }
    },
    [navigate, id],
  )

  if (pending) return <Loading large />

  const reportPane = (
    <div className={classes.pdfBox}>
      <Tabs defaultActiveKey="transmission" className="tabs">
        {alert?.rawFilePath && (
          <TabPane tab="Transmission" key="transmission">
            <PDFPreview path={alert.rawFilePath} perPage={10} />
          </TabPane>
        )}
        {alert?.additionalFilePath && (
          <TabPane tab="Additional PDF" key="additional-pdf">
            <PDFPreview path={alert.additionalFilePath} perPage={10} />
          </TabPane>
        )}
      </Tabs>
    </div>
  )

  const formPane = (
    <div className={classes.formBox}>
      <AlertForm
        form={{ ...alert.form, critical: alert.critical }}
        onSubmit={onSubmit}
      />
    </div>
  )

  const patientPane = (
    <div className={classes.patientContainer}>
      <h3>Patient Information</h3>
      <Descriptions column={1} size="small" layout="vertical">
        <Descriptions.Item label="Device Type">
          {alert.device.deviceModel.deviceType.name}
        </Descriptions.Item>
        <Descriptions.Item label="Device Serial Number">
          {alert.device.serialNumber}
        </Descriptions.Item>
      </Descriptions>
      <TransmissionSidebar
        patient={alert.patient}
        practiceId={alert.patient.practiceId}
      />
    </div>
  )

  // The form panel is pinned to 300px, so on a phone the splitter leaves the
  // PDF around 200px — narrower than the form beside it. Below md the panels
  // are tabs, form first, the patient among them — so the toggle that opens it
  // beside the form is desktop-only.

  return (
    <div className={classes.app}>
      <PageHeader
        title="Edit Alert"
        onBack={() => navigate(-1)}
        extra={
          !isMobile && (
            <Button onClick={togglePatientDrawer}>
              {isPatientDrawerVisible ? 'Hide' : 'Show'} Patient
            </Button>
          )
        }
      />
      {isMobile ? (
        <Tabs activeKey={mobileTab} onChange={setMobileTab}>
          <TabPane tab="Alert Form" key="form">
            {formPane}
          </TabPane>
          <TabPane tab="Report" key="report">
            {reportPane}
          </TabPane>
          <TabPane tab="Patient" key="patient">
            {patientPane}
          </TabPane>
        </Tabs>
      ) : (
        <Splitter>
          <Splitter.Panel>{reportPane}</Splitter.Panel>
          <Splitter.Panel size={300}>{formPane}</Splitter.Panel>
          <Splitter.Panel
            size={isPatientDrawerVisible ? 300 : 0}
            resizable={false}
          >
            {patientPane}
          </Splitter.Panel>
        </Splitter>
      )}
    </div>
  )
}
